import {
  H4,
  FormGroup,
  HTMLSelect,
  NumericInput,
  InputGroup,
  Dialog,
  ProgressBar
} from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import { ReactNode, useState, useRef, useEffect, useMemo } from 'react'
import { Button } from 'components/Button'
import {
  runEncoderCalibration,
  runHallSensorCalibration,
  getSensorCalStatus
} from 'connections/controller'
import { Colors } from 'design/colors'
import { Device, useDevices } from 'hooks/useDevices'
import {
  Strings,
  Env,
  sensorCalibrationStatusToString,
  hallSensorDirectionToString,
  sensorCalibrationFailed
} from 'utils/common'
import {
  SensorCalibStatus,
  EncoderDirection,
  HallsensorDirection,
  IEncoderCalResultResponse,
  IHallSensorCalResultResponse,
  IGetSensorCalStatusResponse,
  PacketId,
  SensorMode,
  fullDisplayUnits
} from 'utils/common-types'
import { isSensorlessEnabled } from 'utils/feature-flags'
import { IEditableSensorsConfig, INonEditableSensorsConfig, SensorsConfig } from 'utils/firestore'
import {
  INavigationProps,
  Edit,
  FORM_WIDTH,
  inputStyle,
  configContainerStyle,
  configModalStyle,
  screenTitleStyle
} from './CStoreEditor'
import { useSubActuators } from 'hooks/useSubActuators'
import { AngleErrorPlot, AngleErrorSample } from 'components/widgets/AngleErrorPlot'

const DEFAULT_OPEN_LOOP_ACCEL_MKRADPS2 = 10 // mech. rad/s^2
const MAX_OPEN_LOOP_ACCEL_MKRADPS2 = 2000 // mech. rad/s^2
const MAX_ANGLE_ERROR_SAMPLES = 120
const CAL_STATS_POLL_INTERVAL_MS = 250
const MIN_CALIBRATION_SPEED_ELRADPS = 600

/*
 * Relevant device commands:
 *
 * Command-response:
 *
 *   S Labs sends START_ENCODER_CAL to start calibration. When completed or failed, controller replies with ENCODER_CAL_RESULT.
 *
 * Asynchronous:
 *
 *   At any time, S Labs may send a GET_ENCODER_CAL_RESULT request to controller. Controller replies with GET_ENCODER_CAL_RESULT response.
 *
 * */
export const SensorsScreen = (
  props: INavigationProps & {
    storedSensorsConfig: SensorsConfig
    editableSensorsConfig: Strings<IEditableSensorsConfig>
    editSensorsConfig: Edit<IEditableSensorsConfig>
    nonEditableSensorsConfig: INonEditableSensorsConfig // Updated by setEncoderState & setHallSensorState
    setNonEditableSensorsConfig: (cfg: INonEditableSensorsConfig) => void
    setWarningContents: (contents: ReactNode) => void
    attached: boolean
  }
) => {
  const { connectedDevice } = useDevices()
  const subActuators = useSubActuators()
  const [isRunning, _setIsRunning] = useState<boolean>(false)
  const [showWarningModal, setShowWarningModal] = useState<false | { motorIndex: number }>(false)
  const {
    setEnableNavigation,
    next,
    prev,
    storedSensorsConfig,
    editableSensorsConfig,
    editSensorsConfig,
    nonEditableSensorsConfig,
    setNonEditableSensorsConfig,
    setWarningContents,
    attached
  } = props
  const selectedSensorMode = parseInt(editableSensorsConfig.sensorMode, 10) as SensorMode
  const [showCalibrationResultMessage, setShowCalibrationResultMessage] = useState<boolean>(false)
  const [sensorStatus, setSensorStatus] = useState<SensorCalibStatus | string | undefined>() // string is for result validation failures

  const [calStats, setCalStats] = useState<IGetSensorCalStatusResponse | null>(null)
  const [angleErrorSamples, setAngleErrorSamples] = useState<AngleErrorSample[]>([])
  // Combined forward+reverse sampling stats; standstill is shown separately below.
  // TODO: Split forward vs reverse once salient-types exposes a phase declaration.
  const [samplingStats, setSamplingStats] = useState<PhaseStats | null>(null)

  const showCalStats =
    selectedSensorMode === SensorMode.ENCODER ||
    selectedSensorMode === SensorMode.ABSOLUTE_ENCODER ||
    selectedSensorMode === SensorMode.HALLSENSOR

  useEffect(() => {
    if (!showCalStats) {
      setCalStats(null)
      setSamplingStats((prev) => (prev === null ? prev : null))
      setAngleErrorSamples((prev) => (prev.length === 0 ? prev : []))
      return
    }
    if (!attached) {
      // Keep last-seen stats/plot visible as stale state; just stop polling
      return
    }
    let cancelled = false
    let timeoutId: ReturnType<typeof setTimeout>
    const poll = async () => {
      if (connectedDevice === undefined || !connectedDevice.initialized) {
        setCalStats(null)
      } else {
        try {
          const stats = await getSensorCalStatus(connectedDevice.handle)
          setCalStats(stats)

          if (stats.calStatus === SensorCalibStatus.ONGOING_SAMPLING && stats.sampleCount > 0) {
            setSamplingStats({
              meanDeg: stats.meanAngleErrorDeg,
              stddevDeg: stats.stddevAngleErrorDeg,
              sampleCount: stats.sampleCount
            })
          }

          // Clear at calibration start; zero samples while calibrating marks the settling window.
          // Gated to avoid re-rendering each poll here.
          if (isCalibrating(stats.calStatus) && stats.sampleCount === 0) {
            setSamplingStats((prev) => (prev === null ? prev : null))
            setAngleErrorSamples((prev) => (prev.length === 0 ? prev : []))
          }

          // TODO: Set isOffsetPhase for standstill samples (re-draws the plot's offset zone)
          // once salient-types exposes a phase declaration.
          if (isFinite(stats.lastAngleErrorDeg) && isCalibrating(stats.calStatus)) {
            setAngleErrorSamples((prev) => {
              const next = [
                ...prev,
                {
                  timestampMs: Date.now(),
                  valueDeg: stats.lastAngleErrorDeg
                }
              ]
              return next.length > MAX_ANGLE_ERROR_SAMPLES
                ? next.slice(-MAX_ANGLE_ERROR_SAMPLES)
                : next
            })
          }
        } catch {
          // Polling failure is non-fatal
        }
      }
      if (!cancelled) {
        timeoutId = setTimeout(poll, CAL_STATS_POLL_INTERVAL_MS)
      }
    }
    poll()
    return () => {
      cancelled = true
      clearTimeout(timeoutId)
    }
  }, [showCalStats, attached, connectedDevice])

  // Clear calibration state when a device (re)attaches so prior failures don't linger across reconnects.
  useEffect(() => {
    if (!attached) return
    setCalStats(null)
    setSamplingStats(null)
    setAngleErrorSamples([])
    setSensorStatus(undefined)
    setShowCalibrationResultMessage(false)
  }, [attached, connectedDevice?.handle])

  const isHallSensorSupported =
    isFinite(nonEditableSensorsConfig.hallsensorForwardOffset) &&
    isFinite(nonEditableSensorsConfig.hallsensorReverseOffset) &&
    nonEditableSensorsConfig.hallsensorDirection !== HallsensorDirection.UNKNOWN

  const lastValidEncoderCprRef = useRef<string>('0')
  const lastValidAccelerationRampRef = useRef<string>('0')

  const handleSensorModeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const sensorMode = e.target.value
    editSensorsConfig('sensorMode', sensorMode)

    // Ensure there is a valid encoder CPR before switching away from Encoder sensor mode
    if (
      selectedSensorMode !== SensorMode.ENCODER &&
      !encoderCprIsValid(editableSensorsConfig.encoderCpr)
    ) {
      // Revert to most recent valid encoderCpr
      editSensorsConfig('encoderCpr', lastValidEncoderCprRef.current)
    }

    // Ensure there is a valid open loop acceleration ramp before switching away from Open Loop sensor mode
    if (
      selectedSensorMode !== SensorMode.OPENLOOP &&
      !openLoopAccelerationIsValid(editableSensorsConfig.accelerationRamp_mkradps2)
    ) {
      // Revert to most recent valid acceleration ramp
      editSensorsConfig('accelerationRamp_mkradps2', lastValidAccelerationRampRef.current)
    }
  }

  // Wraps editSensorsConfig so that the most recent valid encoderCpr is available to be reverted to later.
  const updateEncoderCpr = (newEncoderCpr: string) => {
    if (encoderCprIsValid(newEncoderCpr)) {
      lastValidEncoderCprRef.current = newEncoderCpr
    }
    editSensorsConfig('encoderCpr', newEncoderCpr)
  }

  // Wraps editSensorsConfig so that the most recent valid accelerationRamp_mkradps2 is available to be reverted to later.
  const updateAccelerationRamp = (newAccelerationRamp: string) => {
    if (openLoopAccelerationIsValid(newAccelerationRamp)) {
      lastValidAccelerationRampRef.current = newAccelerationRamp
    }
    editSensorsConfig('accelerationRamp_mkradps2', newAccelerationRamp)
  }

  const uiSensorModeLabels: Partial<Record<SensorMode, string>> = {
    [SensorMode.SENSORLESS]: 'Sensorless HFI',
    [SensorMode.ENCODER]: 'Encoder',
    [SensorMode.OPENLOOP]: 'Open Loop Startup',
    [SensorMode.HALLSENSOR]: 'Hall Sensor',
    [SensorMode.ABSOLUTE_ENCODER]: 'Absolute Encoder',
    [SensorMode.RESOLVER]: 'Resolver'
  }
  let sensorModeOptions = Object.keys(SensorMode)
    .filter((k) => isFinite(Number(k)))
    .map((key, _) => {
      let mode: SensorMode = Number(key)
      return {
        value: mode,
        label: uiSensorModeLabels[mode] ?? SensorMode[mode]
      }
    })

  // Conditionally remove two of these assuming they aren't currently selected.
  if (!isSensorlessEnabled() && selectedSensorMode !== SensorMode.SENSORLESS) {
    sensorModeOptions = sensorModeOptions.filter((opt) => opt.value !== SensorMode.SENSORLESS)
  }
  if (!isHallSensorSupported && selectedSensorMode !== SensorMode.HALLSENSOR) {
    sensorModeOptions = sensorModeOptions.filter((opt) => opt.value !== SensorMode.HALLSENSOR)
  }

  // Sensorless HFI is disallowed for legal reasons.
  const showSensorlessNotice =
    isSensorlessEnabled() &&
    !isSensorlessEnabled(Env.Prod) &&
    selectedSensorMode === SensorMode.SENSORLESS

  const setIsRunning = (isRunning: boolean) => {
    _setIsRunning(isRunning)
    setEnableNavigation(!isRunning)
  }

  // Calibration
  // Calibration per motor index
  const calibrate = async (motorIndex: number) => {
    if (connectedDevice === undefined || !connectedDevice.initialized) {
      return
    }

    setIsRunning(true)
    if (
      selectedSensorMode !== SensorMode.ENCODER &&
      selectedSensorMode !== SensorMode.HALLSENSOR &&
      selectedSensorMode !== SensorMode.ABSOLUTE_ENCODER &&
      selectedSensorMode !== SensorMode.RESOLVER
    ) {
      throw new Error(`Sensor mode ${SensorMode[selectedSensorMode]} is not implemented!`)
    }
    try {
      let results: IEncoderCalResultResponse | IHallSensorCalResultResponse | undefined

      if (
        selectedSensorMode === SensorMode.ENCODER ||
        selectedSensorMode === SensorMode.ABSOLUTE_ENCODER
      ) {
        results = await runEncoderCalibration(connectedDevice.handle, motorIndex)
        if (
          results.status === SensorCalibStatus.COMPLETE &&
          (!isFinite(results.offset) || results.direction === EncoderDirection.UNKNOWN)
        ) {
          setSensorStatus(
            `Encoder calibration results have invalid data. Please inform Salient Motion Support. Data: ${JSON.stringify(results)}`
          )
        } else {
          setSensorStatus(results.status)
          setNonEditableSensorsConfig({
            ...nonEditableSensorsConfig,
            encoderDirection: results.direction,
            encoderOffset: results.offset
          })
        }
      } else if (selectedSensorMode === SensorMode.RESOLVER) {
        results = await runEncoderCalibration(connectedDevice.handle, motorIndex)
        if (
          results.status === SensorCalibStatus.COMPLETE &&
          (!isFinite(results.offset) || results.direction === EncoderDirection.UNKNOWN)
        ) {
          setSensorStatus(
            `Resolver calibration results have invalid data. Please inform Salient Motion Support. Data: ${JSON.stringify(results)}`
          )
        } else {
          setSensorStatus(results.status)
          setNonEditableSensorsConfig({
            ...nonEditableSensorsConfig,
            resolverDirection: results.direction,
            resolverOffset: results.offset
          })
        }
      } else {
        results = isHallSensorSupported
          ? await runHallSensorCalibration(connectedDevice.handle, motorIndex)
          : undefined
        if (
          results?.status === SensorCalibStatus.COMPLETE &&
          (!isFinite(results.forwardOffset) ||
            !isFinite(results.reverseOffset) ||
            results.direction === HallsensorDirection.UNKNOWN)
        ) {
          setSensorStatus(
            `Hall sensor calibration results have invalid data. Please inform Salient Motion Support. Data: ${JSON.stringify(results)}`
          )
        } else if (isDefined(results)) {
          setNonEditableSensorsConfig({
            ...nonEditableSensorsConfig,
            hallsensorDirection: results.direction,
            hallsensorForwardOffset: results.forwardOffset,
            hallsensorReverseOffset: results.reverseOffset
          })
          setSensorStatus(results.status)
        } else {
          setSensorStatus(undefined)
        }
      }
      if (results === undefined || results.status !== SensorCalibStatus.COMPLETE) {
        // Need to show something to user
        setShowCalibrationResultMessage(true)
      }
    } catch (e) {
      console.warn('Exception from calibration:', e)
      const errorMsg = 'Failure: No response from controller — is a motor connected?'
      if (
        selectedSensorMode === SensorMode.ENCODER ||
        selectedSensorMode === SensorMode.ABSOLUTE_ENCODER ||
        selectedSensorMode === SensorMode.RESOLVER
      ) {
        setSensorStatus(errorMsg)
      } else if (selectedSensorMode === SensorMode.HALLSENSOR) {
        setSensorStatus(errorMsg)
      }
      setShowCalibrationResultMessage(true)
    } finally {
      setIsRunning(false)
    }
  }

  const encoderSelected = selectedSensorMode === SensorMode.ENCODER
  const resolverSelected = selectedSensorMode === SensorMode.RESOLVER
  const openLoopSelected = selectedSensorMode === SensorMode.OPENLOOP
  const hallSensorSelected = selectedSensorMode === SensorMode.HALLSENSOR
  const absoluteEncoderSelected = selectedSensorMode === SensorMode.ABSOLUTE_ENCODER

  const forwardOffsetUnits = fullDisplayUnits[PacketId.HALL_SENSOR_CAL_RESULT]['forwardOffset']
  const reverseOffsetUnits = fullDisplayUnits[PacketId.HALL_SENSOR_CAL_RESULT]['reverseOffset']
  const isTimeout = sensorStatus === SensorCalibStatus.FAILURE_TIMEOUT
  const statusStr =
    sensorStatus !== undefined ? sensorCalibrationStatusToString(sensorStatus) : '---'
  const encoderOffsetStr = isFinite(nonEditableSensorsConfig.encoderOffset)
    ? nonEditableSensorsConfig.encoderOffset.toExponential(6)
    : undefined
  const resolverOffsetStr =
    nonEditableSensorsConfig.resolverOffset !== undefined &&
    isFinite(nonEditableSensorsConfig.resolverOffset)
      ? nonEditableSensorsConfig.resolverOffset.toExponential(6)
      : undefined
  const hallSensorForwardOffsetStr = isFinite(nonEditableSensorsConfig.hallsensorForwardOffset)
    ? nonEditableSensorsConfig.hallsensorForwardOffset.toExponential(6)
    : undefined
  const hallSensorReverseOffsetStr = isFinite(nonEditableSensorsConfig.hallsensorReverseOffset)
    ? nonEditableSensorsConfig.hallsensorReverseOffset.toExponential(6)
    : undefined
  const hallSensorDirectionStr =
    hallSensorDirectionToString(nonEditableSensorsConfig.hallsensorDirection) ?? '---'

  let encoderDirectionStr = undefined
  if (nonEditableSensorsConfig.encoderDirection !== EncoderDirection.UNKNOWN) {
    if (nonEditableSensorsConfig.encoderDirection === EncoderDirection.COUNT_UP) {
      encoderDirectionStr = 'Forward'
    } else if (nonEditableSensorsConfig.encoderDirection === EncoderDirection.COUNT_DOWN) {
      encoderDirectionStr = 'Reversed'
    }
  }

  let resolverDirectionStr = undefined
  if (
    nonEditableSensorsConfig.resolverDirection !== undefined &&
    nonEditableSensorsConfig.resolverDirection !== EncoderDirection.UNKNOWN
  ) {
    if (nonEditableSensorsConfig.resolverDirection === EncoderDirection.COUNT_UP) {
      resolverDirectionStr = 'Forward'
    } else if (nonEditableSensorsConfig.resolverDirection === EncoderDirection.COUNT_DOWN) {
      resolverDirectionStr = 'Reversed'
    }
  }

  const calibrateErrorsMemo = useMemo(
    () => calibrateErrors(connectedDevice, editableSensorsConfig, storedSensorsConfig),
    [connectedDevice, editableSensorsConfig, storedSensorsConfig]
  )
  const calibrateWarningsMemo: string[] = useMemo(() => {
    let warnings = []
    if (
      selectedSensorMode === SensorMode.ENCODER ||
      selectedSensorMode === SensorMode.ABSOLUTE_ENCODER
    ) {
      const storedSensorMode = storedSensorsConfig.sensorMode
      const isValidOffset = isFinite(nonEditableSensorsConfig.encoderOffset)
      const isValidDirection =
        nonEditableSensorsConfig.encoderDirection !== EncoderDirection.UNKNOWN
      if (!isValidOffset || !isValidDirection) {
        if (
          storedSensorMode === SensorMode.ENCODER ||
          storedSensorMode === SensorMode.ABSOLUTE_ENCODER
        ) {
          warnings.push(
            'Before attempting to command motor, first calibrate, then Export and Update Config on device.'
          )
        } else {
          warnings.push(
            'After Exporting and Updating Config on device, return here and Calibrate Encoder!'
          )
        }
      }
    }
    return warnings
  }, [storedSensorsConfig, nonEditableSensorsConfig, selectedSensorMode])
  const sensorErrorsMemo = useMemo(
    () => sensorErrors(connectedDevice, editableSensorsConfig),
    [connectedDevice, editableSensorsConfig]
  )
  const calibrationBlocked = calibrateErrorsMemo.length > 0

  useEffect(() => {
    const warningsArray = calibrateWarningsMemo
    const messagesArray =
      encoderSelected || hallSensorSelected || absoluteEncoderSelected
        ? [...calibrateErrorsMemo, ...warningsArray]
        : sensorErrorsMemo
    if (messagesArray.length === 0) {
      setWarningContents(null)
      return
    }

    const sensorsWarning = (
      <>
        <H4>
          {encoderSelected || absoluteEncoderSelected
            ? messagesArray.length === warningsArray.length
              ? 'Warning'
              : 'Calibration Blocked'
            : 'Unknown Error'}
        </H4>
        <ul style={{ color: Colors.WHITE }}>
          {messagesArray.map((msg, i) => (
            <li key={i}>{msg}</li>
          ))}
        </ul>
      </>
    )
    setWarningContents(sensorsWarning)
  }, [
    setWarningContents,
    calibrationBlocked,
    calibrateErrorsMemo,
    calibrateWarningsMemo,
    sensorErrorsMemo,
    encoderSelected,
    hallSensorSelected,
    openLoopSelected,
    absoluteEncoderSelected
  ])

  const calStatsPanel = showCalStats &&
    calStats !== null &&
    (isRunning || absoluteEncoderSelected) && (
      <div style={{ width: FORM_WIDTH, display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 'bold',
            color: Colors.WHITE,
            marginBottom: 8
          }}
        >
          Calibration Statistics
        </div>

        {/* Phase/progress reflect a live run only; end/idle states show nothing here. */}
        {isRunning ? (
          isCalibrating(calStats.calStatus) ? (
            <FormGroup label={`Phase: ${sensorCalibrationStatusToString(calStats.calStatus)}`}>
              {/* Indeterminate bar: firmware reports no progress percentage. */}
              <ProgressBar
                intent={
                  calStats.calStatus === SensorCalibStatus.ONGOING_SAMPLING ? 'success' : 'primary'
                }
                stripes={true}
              />
            </FormGroup>
          ) : (
            <FormGroup label="Running...">
              <ProgressBar intent="primary" stripes={true} />
            </FormGroup>
          )
        ) : null}

        {/* Standstill D-axis lock (absolute-encoder only); offset and noise stats come straight
          from firmware's d_axis_offset_rad / sensor_noise_* fields. */}
        {absoluteEncoderSelected &&
          (isFinite(calStats.dAxisOffsetRad) || calStats.sampleCount > 0) && (
            <>
              <SectionHeader color={Colors.YELLOW}>
                {isFinite(calStats.dAxisOffsetRad)
                  ? 'Standstill D-Axis Lock (offset source)'
                  : 'Standstill D-Axis Lock (offset not set)'}
              </SectionHeader>
              <ReadOnlyField
                label="D-Axis Offset (radians)"
                value={
                  isFinite(calStats.dAxisOffsetRad)
                    ? calStats.dAxisOffsetRad.toExponential(6)
                    : undefined
                }
                placeholder="---"
              />
              <ReadOnlyField label="Mean" value={formatDeg(calStats.sensorNoiseMeanDeg)} />
              <ReadOnlyField
                label="Std Dev"
                value={formatDeg(calStats.sensorNoiseStddevDeg)}
                valueColor={calStats.sensorNoiseStddevDeg > 5.0 ? Colors.RED : undefined}
              />
            </>
          )}

        {/* Sampling stats are live-run only; hidden in end/idle states so stale values don't linger.
          TODO: Split into Forward / Reverse once salient-types exposes a phase declaration. */}
        {isRunning && <PhaseStatsBlock title="Sampling" stats={samplingStats} />}
      </div>
    )

  // Needs >= 2 points to draw a line; below that the plot only shows an empty "Waiting" box.
  const anglePlotPanel = showCalStats &&
    // only encoders currently accurately report angleError
    (selectedSensorMode === SensorMode.ENCODER ||
      selectedSensorMode === SensorMode.ABSOLUTE_ENCODER) &&
    angleErrorSamples.length > 1 && (
      <div style={{ width: 2 * FORM_WIDTH + 24, maxWidth: '100%', marginTop: 16 }}>
        <AngleErrorPlot samples={angleErrorSamples} width={2 * FORM_WIDTH + 24} height={200} />
      </div>
    )

  return (
    <>
      <div style={configContainerStyle}>
        <div style={screenTitleStyle}>Sensors Configuration</div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: 24,
            alignItems: 'flex-start',
            justifyContent: 'center'
          }}
        >
          <div
            style={{
              width: FORM_WIDTH,
              display: 'flex',
              flexDirection: 'column',
              marginBottom: 16
            }}
          >
            <div style={{ display: 'flex' }}>
              <FormGroup label="Sensor Mode">
                <HTMLSelect
                  style={{
                    ...inputStyle,
                    width: FORM_WIDTH
                  }}
                  fill={true}
                  value={selectedSensorMode}
                  options={sensorModeOptions}
                  onChange={handleSensorModeChange}
                />
              </FormGroup>
            </div>

            {showSensorlessNotice && (
              <div style={{ color: Colors.YELLOW }}>
                <p>Note: Sensorless HFI mode is disabled in production.</p>
              </div>
            )}

            {/* Inputs if encoder, resolver, or absolute encoder sensor mode is selected */}
            {(encoderSelected || resolverSelected || absoluteEncoderSelected) && (
              <>
                <FormGroup
                  label={
                    resolverSelected
                      ? 'Resolver CPR (counts per revolution)'
                      : encoderSelected
                        ? 'Encoder CPR (counts per revolution)'
                        : 'Absolute Encoder CPR (counts per revolution)'
                  }
                  labelInfo={<span style={{ color: Colors.RED }}>*</span>}
                >
                  <NumericInput
                    fill={true}
                    value={editableSensorsConfig.encoderCpr}
                    onValueChange={(_, valStr) => updateEncoderCpr(valStr)}
                    stepSize={
                      1e-15 /* accept any floating point for now; integer validation happens later */
                    }
                    minorStepSize={null}
                    buttonPosition="none"
                    placeholder={
                      resolverSelected
                        ? 'Resolver CPR'
                        : encoderSelected
                          ? 'Encoder CPR'
                          : 'Absolute Encoder CPR'
                    }
                    style={inputStyle}
                  />
                </FormGroup>
                <ReadOnlyField
                  label={
                    resolverSelected
                      ? 'Resolver Status'
                      : encoderSelected
                        ? 'Encoder Status'
                        : 'Absolute Encoder Status'
                  }
                  value={statusStr}
                  placeholder="---"
                  valueColor={sensorCalibrationFailed(sensorStatus) ? Colors.RED : undefined}
                />
                {sensorStatus === SensorCalibStatus.FAILURE_POSITION_ERROR && (
                  <div style={{ color: Colors.YELLOW, fontSize: 13, marginBottom: 8 }}>
                    Position error may indicate the calibration speed is too low. Try increasing the
                    Calibration Speed below.
                  </div>
                )}
                <ReadOnlyField
                  label={`${resolverSelected ? 'Resolver' : encoderSelected ? 'Encoder' : 'Absolute Encoder'} Offset (electrical radians)`}
                  value={resolverSelected ? resolverOffsetStr : encoderOffsetStr}
                  placeholder="---"
                />
                {/* Encoder Direction is read-only here because it's only changeable in All Parameters for advanced users. Customers should not alter this. */}
                <ReadOnlyField
                  label={`${resolverSelected ? 'Resolver' : encoderSelected ? 'Encoder' : 'Absolute Encoder'} Direction`}
                  value={resolverSelected ? resolverDirectionStr : encoderDirectionStr}
                  placeholder="---"
                />
              </>
            )}

            {/* Hall sensor mode is selected */}
            {hallSensorSelected && (
              <>
                <ReadOnlyField
                  label="Sensor Status"
                  value={statusStr}
                  placeholder="---"
                  valueColor={sensorCalibrationFailed(sensorStatus) ? Colors.RED : undefined}
                />
                <ReadOnlyField
                  label={`Hall Sensor Forward Offset (${forwardOffsetUnits})`}
                  value={hallSensorForwardOffsetStr}
                  placeholder="---"
                />
                <ReadOnlyField
                  label={`Hall Sensor Reverse Offset (${reverseOffsetUnits})`}
                  value={hallSensorReverseOffsetStr}
                  placeholder="---"
                />
                <ReadOnlyField
                  label="Hall Sensor Direction"
                  value={hallSensorDirectionStr}
                  placeholder="---"
                />
              </>
            )}

            {/* Inputs if open loop sensor mode is selected */}
            {openLoopSelected && (
              <>
                <FormGroup
                  label="Motor Open Loop Acceleration (mech. rad/s^2)"
                  labelInfo={<span style={{ color: Colors.RED }}>*</span>}
                >
                  <NumericInput
                    fill={true}
                    value={editableSensorsConfig.accelerationRamp_mkradps2}
                    onValueChange={(_, valStr) => updateAccelerationRamp(valStr)}
                    stepSize={1e-15 /* accept any floating point */}
                    minorStepSize={null}
                    buttonPosition="none"
                    placeholder={DEFAULT_OPEN_LOOP_ACCEL_MKRADPS2.toString()}
                    style={inputStyle}
                  />
                </FormGroup>
              </>
            )}
          </div>

          {/* Calibration Statistics — beside configuration (hidden inline while a calibration is running; shown in the modal instead) */}
          {!isRunning && calStatsPanel}
        </div>

        {/* Angle Error Plot — full width below the two columns (hidden inline while running; shown in modal) */}
        {!isRunning && anglePlotPanel}
        {/* Calibrate buttons for each motor index if encoder, resolver, hall sensor, or absolute encoder sensor mode is selected */}
        {(encoderSelected || resolverSelected || hallSensorSelected || absoluteEncoderSelected) && (
          <>
            <FormGroup
              label={
                <span>
                  Calibration Speed{' '}
                  <span style={{ opacity: 0.6, fontSize: 12 }}>(elec. rad/s)</span>
                </span>
              }
              labelInfo={<span style={{ color: Colors.RED }}>*</span>}
              style={{ width: FORM_WIDTH }}
            >
              <NumericInput
                fill={true}
                value={editableSensorsConfig.calibrationSpeed_elradps}
                onValueChange={(_, valStr) => editSensorsConfig('calibrationSpeed_elradps', valStr)}
                stepSize={1e-15}
                minorStepSize={null}
                buttonPosition="none"
                placeholder="1000"
                min={MIN_CALIBRATION_SPEED_ELRADPS}
                style={inputStyle}
              />
            </FormGroup>
            <div
              style={{
                color: Colors.YELLOW,
                paddingLeft: 8,
                paddingRight: 8,
                display: 'flex'
              }}
            >
              <span style={{ fontWeight: 'bold' }}>Warning:&nbsp;</span> After clicking "Calibrate"
              below and confirming prompt, the connected motor will spin.
            </div>
            <div style={{ height: 16 }} />
            <div style={{ display: 'flex', flexDirection: 'row', gap: 16, width: FORM_WIDTH }}>
              <div style={{ flex: 1 }}>
                <Tooltip2
                  content="A device must be actively connected to use this function"
                  disabled={attached}
                  targetTagName="div"
                >
                  <Button
                    backgroundColor={Colors.BUTTON_GREEN}
                    textColor={Colors.WHITE}
                    disabled={!attached || isRunning || calibrationBlocked}
                    onClick={() => setShowWarningModal({ motorIndex: 0 })}
                  >
                    Calibrate Motor{Object.keys(subActuators).length > 0 ? ' 0' : ''}
                  </Button>
                </Tooltip2>
              </div>
              {Object.keys(subActuators).length > 0 && (
                <div style={{ flex: 1 }}>
                  <Tooltip2
                    content="A device must be actively connected to use this function"
                    disabled={attached}
                    targetTagName="div"
                  >
                    <Button
                      backgroundColor={Colors.BUTTON_GREEN}
                      textColor={Colors.WHITE}
                      disabled={!attached || isRunning || calibrationBlocked}
                      onClick={() => setShowWarningModal({ motorIndex: 1 })}
                    >
                      Calibrate Motor 1
                    </Button>
                  </Tooltip2>
                </div>
              )}
            </div>
            <div style={{ height: 16 }} />
          </>
        )}
        <div style={{ display: 'flex', width: FORM_WIDTH }}>
          <div style={{ flex: 1 }}>
            <Button
              backgroundColor={Colors.WHITE}
              textColor={Colors.BLACK}
              onClick={prev}
              disabled={prev === undefined}
            >
              Back
            </Button>
          </div>
          <div style={{ width: 8 }} />
          <div style={{ flex: 1 }} />
          <div style={{ width: 8 }} />
          <div style={{ flex: 1 }}>
            <Button
              backgroundColor={Colors.BUTTON_GREEN}
              textColor={Colors.WHITE}
              onClick={next}
              disabled={next === undefined}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
      <Dialog
        title="Calibration In Progress"
        isOpen={isRunning}
        canEscapeKeyClose={false}
        canOutsideClickClose={false}
        isCloseButtonShown={false}
        style={{
          backgroundColor: Colors.LIGHT_GREEN,
          width: 'auto',
          maxWidth: '90vw'
        }}
      >
        <div style={configModalStyle}>
          {calStats === null ? (
            <div style={{ width: FORM_WIDTH }}>
              <FormGroup label="Waiting for controller…">
                <ProgressBar intent="primary" stripes={true} />
              </FormGroup>
              <div style={{ color: Colors.YELLOW, fontSize: 13 }}>
                If no progress appears, the motor may not be connected. Calibration will time out
                after 2 minutes.
              </div>
            </div>
          ) : (
            <>
              {calStatsPanel}
              {anglePlotPanel}
            </>
          )}
        </div>
      </Dialog>
      <Dialog
        title={'Calibration ' + (isTimeout ? 'Timeout' : 'Failure')}
        isOpen={showCalibrationResultMessage}
        onClose={() => setShowCalibrationResultMessage(false)}
        style={{ backgroundColor: Colors.LIGHT_GREEN }}
      >
        <div style={configModalStyle}>
          {selectedSensorMode === SensorMode.OPENLOOP ||
          selectedSensorMode === SensorMode.RESOLVER ? (
            <p> 'Unknown calibration error'</p>
          ) : (
            <p>
              A problem was detected during calibration —{' '}
              {sensorStatus && sensorCalibrationStatusToString(sensorStatus)}.
            </p>
          )}
        </div>
      </Dialog>
      <Dialog
        title="Warning"
        isOpen={!!showWarningModal}
        onClose={() => setShowWarningModal(false)}
        style={{ backgroundColor: Colors.LIGHT_GREEN }}
      >
        <div style={configModalStyle}>
          <p>
            Motor will spin if you select Run Calibration, below!
            <br />
            <span style={{ color: Colors.YELLOW }}>
              <b>Motor must run unloaded!</b>
            </span>
          </p>
          <div style={{ alignSelf: 'flex-end', display: 'flex' }}>
            <Button
              backgroundColor={Colors.RED}
              textColor={Colors.BLACK}
              style={{ marginRight: 10 }}
              onClick={() => {
                setShowWarningModal(false)
              }}
            >
              Cancel
            </Button>
            {showWarningModal &&
              typeof showWarningModal === 'object' &&
              showWarningModal.motorIndex !== undefined && (
                <Button
                  backgroundColor={Colors.WHITE}
                  textColor={Colors.BLACK}
                  onClick={async () => {
                    setShowWarningModal(false)
                    await calibrate(showWarningModal.motorIndex)
                  }}
                >
                  Run Calibration (Motor {showWarningModal.motorIndex})
                </Button>
              )}
          </div>
        </div>
      </Dialog>
    </>
  )
}

export const sensorsAreValid = (
  editableSensorsConfig: Strings<IEditableSensorsConfig>
): boolean => {
  const selectedSensorMode = parseInt(editableSensorsConfig.sensorMode, 10) as SensorMode

  switch (selectedSensorMode) {
    case SensorMode.ENCODER:
      return true
    case SensorMode.OPENLOOP:
      return openLoopAccelerationIsValid(editableSensorsConfig.accelerationRamp_mkradps2)
    case SensorMode.SENSORLESS:
      return isSensorlessEnabled()
    case SensorMode.HALLSENSOR:
      return true
    case SensorMode.ABSOLUTE_ENCODER:
      return encoderCprIsValid(editableSensorsConfig.encoderCpr)
    case SensorMode.RESOLVER:
      return true // Allow export for resolver mode
    default:
      return false
  }
}

const sensorErrors = (
  connectedDevice: Device | undefined,
  editableSensorsConfig: Strings<IEditableSensorsConfig>
): string[] => {
  let errors = []

  if (connectedDevice === undefined || !connectedDevice.initialized) {
    errors.push('A device must be connected.')
  }

  // Check for open loop acceleration ramp validity
  if (!openLoopAccelerationIsValid(editableSensorsConfig.accelerationRamp_mkradps2)) {
    errors.push(
      `Open Loop Acceleration must be a floating point value from 0 to ${MAX_OPEN_LOOP_ACCEL_MKRADPS2} inclusive.`
    )
  }

  return errors
}

// Superset of sensorErrors()
const calibrateErrors = (
  connectedDevice: Device | undefined,
  editableSensorsConfig: Strings<IEditableSensorsConfig>,
  storedSensorsConfig: SensorsConfig
): string[] => {
  let errors = [...sensorErrors(connectedDevice, editableSensorsConfig)]

  const selectedSensorMode = parseInt(editableSensorsConfig.sensorMode, 10) as SensorMode

  const sensorModeNeedsUpdate = selectedSensorMode !== storedSensorsConfig.sensorMode
  if (sensorModeNeedsUpdate) {
    errors.push(
      'The changed sensor mode needs to be Exported and then applied to device with Update Config button'
    )
  }

  // Check for encoder CPR validity
  if (
    (selectedSensorMode === SensorMode.ENCODER ||
      selectedSensorMode === SensorMode.ABSOLUTE_ENCODER) &&
    !encoderCprIsValid(editableSensorsConfig.encoderCpr)
  ) {
    errors.push('Encoder CPR must be an integer from 0 to 8192 inclusive.')
  }

  // Below MIN_CALIBRATION_SPEED_ELRADPS the motor is too noisy to calibrate and will time out
  if (
    (selectedSensorMode === SensorMode.ENCODER ||
      selectedSensorMode === SensorMode.ABSOLUTE_ENCODER ||
      selectedSensorMode === SensorMode.HALLSENSOR) &&
    !(parseFloat(editableSensorsConfig.calibrationSpeed_elradps) >= MIN_CALIBRATION_SPEED_ELRADPS)
  ) {
    errors.push(`Calibration Speed must be at least ${MIN_CALIBRATION_SPEED_ELRADPS} elec. rad/s.`)
  }

  return errors
}

// Input is mech. rad/s^2
const openLoopAccelerationIsValid = (accelerationRampStr_mkradps2: string): boolean => {
  let ramp = parseFloat(accelerationRampStr_mkradps2)
  return ramp >= 0 && ramp <= MAX_OPEN_LOOP_ACCEL_MKRADPS2
}

const encoderCprIsValid = (encoderCprStr: string): boolean => {
  let encoderCpr = parseFloat(encoderCprStr)
  return Number.isInteger(encoderCpr) && 0 <= encoderCpr && encoderCpr <= 8192
}

const formatDeg = (val: number | undefined): string => {
  if (val === undefined || !isFinite(val)) return '---'
  return val.toFixed(3) + '°'
}

type PhaseStats = { meanDeg: number; stddevDeg: number; sampleCount: number }

// Calibration is in progress while calStatus is any of the ONGOING_* stages.
const ONGOING_CAL_STATUSES: readonly SensorCalibStatus[] = [
  SensorCalibStatus.ONGOING,
  SensorCalibStatus.ONGOING_SETTLING,
  SensorCalibStatus.ONGOING_SAMPLING,
  SensorCalibStatus.ONGOING_COMPUTING
]

const isCalibrating = (status: SensorCalibStatus): boolean => ONGOING_CAL_STATUSES.includes(status)

const statsInputStyle = { backgroundColor: Colors.DARK_GREEN, color: Colors.WHITE }

const SectionHeader = ({ color, children }: { color: string; children: ReactNode }) => (
  <div style={{ fontSize: 13, fontWeight: 'bold', color, marginTop: 12, marginBottom: 4 }}>
    {children}
  </div>
)

const ReadOnlyField = ({
  label,
  value,
  valueColor,
  placeholder
}: {
  label: string
  value: string | undefined
  valueColor?: string
  placeholder?: string
}) => (
  <FormGroup label={label}>
    <InputGroup
      disabled={true}
      value={value ?? ''}
      placeholder={placeholder}
      style={valueColor ? { ...statsInputStyle, color: valueColor } : statsInputStyle}
    />
  </FormGroup>
)

const PhaseStatsBlock = ({
  title,
  titleColor = Colors.WHITE,
  stats,
  stddevColor
}: {
  title: string
  titleColor?: string
  stats: PhaseStats | null
  stddevColor?: string
}) => {
  if (stats === null) return null
  return (
    <>
      <SectionHeader color={titleColor}>{title}</SectionHeader>
      <ReadOnlyField label="Mean" value={formatDeg(stats.meanDeg)} />
      <ReadOnlyField label="Std Dev" value={formatDeg(stats.stddevDeg)} valueColor={stddevColor} />
      <ReadOnlyField label="Samples" value={stats.sampleCount.toString()} />
    </>
  )
}
